import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/admin/settings').then(r => setS(r.data)).catch(() => {}); }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/settings', s); toast.success('Settings saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setSaving(false); }
  };

  if (!s) return <div className="text-brand-slate text-sm">Loading…</div>;

  const num = (k, label, hint) => (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-brand-dim">{label}</Label>
      <Input data-testid={`setting-${k}`} type="number" min={1} value={s[k]} onChange={e => setS({ ...s, [k]: Number(e.target.value) })} className="mt-2 rounded-xl max-w-[160px]" />
      {hint && <p className="text-xs text-brand-dim mt-1">{hint}</p>}
    </div>
  );

  const toggleCat = (key) => setS({ ...s, categories: s.categories.map(c => c.key === key ? { ...c, enabled: !c.enabled } : c) });

  return (
    <div data-testid="settings-panel">
      <h2 className="font-display font-bold text-2xl text-brand-ink mb-1">Categories &amp; settings</h2>
      <p className="text-brand-slate text-sm mb-6">Control posting rules across the app.</p>

      <div className="glass-panel rounded-3xl p-6 space-y-5">
        <h3 className="font-display text-lg text-brand-ink">Posting rules</h3>
        {num('max_active_hours', 'Default / max active hours', 'Maximum lifespan of a post (hours).')}
        {num('max_advance_days', 'Max advance days', 'How far ahead a post can be scheduled.')}
        {num('max_photos_per_post', 'Max photos per post', '')}

        <div className="pt-4 mt-2 border-t border-black/5" />
        <h3 className="font-display text-lg text-brand-ink">Categories</h3>
        <div className="space-y-2">
          {s.categories.map(c => (
            <div key={c.key} className="flex items-center justify-between rounded-xl bg-black/5 px-4 py-3">
              <div className="text-sm font-medium text-brand-ink">{c.label}</div>
              <Switch data-testid={`category-toggle-${c.key}`} checked={c.enabled} onCheckedChange={() => toggleCat(c.key)} />
            </div>
          ))}
          <p className="text-xs text-brand-dim">Disabled categories stop accepting new posts but keep existing ones visible.</p>
        </div>

        <div className="pt-2 flex justify-end">
          <Button data-testid="settings-save" onClick={save} disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save settings
          </Button>
        </div>
      </div>
    </div>
  );
}
